import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Sparkles, LogIn, LayoutDashboard } from "lucide-react";
import { getToken, getStoredUser, dashboardPathFor } from "@/lib/auth";

const LINKS = [
  { to: "/marketplace", label: "Marketplace" },
  { to: "/ai-planner", label: "AI Planner" },
  { to: "/cities", label: "Cities" },
  { to: "/industries", label: "Industries" },
  { to: "/case-studies", label: "Work" },
  { to: "/solutions", label: "Solutions" },
  { to: "/resources", label: "Insights" },
];

// Routes whose hero is forced dark — Nav must render in light text on these.
const DARK_ROUTES = ["/ai-planner"];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const user = getToken() ? getStoredUser() : null;
  const dashPath = user ? dashboardPathFor(user) : null;
  const isDarkPage = DARK_ROUTES.some((p) => loc.pathname.startsWith(p));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  const headerBg = scrolled
    ? (isDarkPage ? "glass" : "glass-light")
    : "bg-transparent";

  // Text colour: dark page → white; light page → ink (black)
  const linkColor = isDarkPage ? "text-white" : "text-[var(--ink)]";

  return (
    <header
      data-testid="site-nav"
      data-theme={isDarkPage ? "dark" : "light"}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${headerBg}`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 h-16 lg:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img src="/brand/fundle-logo.png" alt="Fundle" className="h-8 lg:h-9 w-auto" />
          <span className={`hidden md:inline-block text-[10px] tracking-[0.3em] uppercase font-semibold border-l pl-3 ${isDarkPage ? "text-white/80 border-white/20" : "text-[var(--slate-1)] border-[var(--border)]"}`}>
            Space&nbsp;on&nbsp;Hiring
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-3.5 py-2 text-[12px] font-medium tracking-[0.12em] uppercase transition-colors ${
                  isActive ? "text-[var(--gold)]" : `${linkColor} hover:text-[var(--gold)]`
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          {user ? (
            <Link to={dashPath} data-testid="nav-dashboard-btn" className={`btn-ghost inline-flex items-center gap-2 ${isDarkPage ? "!text-white hover:!text-[var(--gold-light)]" : ""}`}>
              <LayoutDashboard size={14} /> Dashboard
            </Link>
          ) : (
            <Link to="/login" data-testid="nav-login-btn" className={`btn-ghost inline-flex items-center gap-2 ${isDarkPage ? "!text-white hover:!text-[var(--gold-light)]" : ""}`}>
              <LogIn size={14} /> Sign in
            </Link>
          )}
          <Link
            to="/ai-planner"
            data-testid="nav-cta-btn"
            className={`btn-primary ${isDarkPage ? "!bg-[var(--bone)] !text-[var(--obsidian)] !border-[var(--bone)] hover:!bg-[var(--gold)] hover:!text-white hover:!border-[var(--gold)]" : ""}`}
          >
            <Sparkles size={14} strokeWidth={2.4} />
            Plan with AI
          </Link>
        </div>

        <button
          aria-label="Open menu"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className={`lg:hidden p-2 ${linkColor}`}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-panel" className={`lg:hidden border-t ${isDarkPage ? "glass border-white/10" : "glass-light border-[var(--border)]"}`}>
          <div className="px-6 py-6 flex flex-col gap-1">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `py-3 text-sm tracking-[0.12em] uppercase font-medium ${isActive ? "text-[var(--gold)]" : linkColor}`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <div className="flex gap-3 pt-4">
              {user ? (
                <Link to={dashPath} className="btn-secondary text-xs flex-1 justify-center">Dashboard</Link>
              ) : (
                <Link to="/login" className="btn-secondary text-xs flex-1 justify-center">Sign in</Link>
              )}
              <Link to="/ai-planner" className="btn-primary text-xs flex-1 justify-center">Plan with AI</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
